import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MapPin, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Pins() {
  const [pins, setPins] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pin_code: "", city: "", state: "", active: true });
  const [check, setCheck] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  const load = async () => {
    const r = await api.get("/pins", { params: { limit: 500, ...(q ? { q } : {}) } });
    const items = r.data.items || r.data || [];
    setPins(items);
    setTotal(typeof r.data.total === "number" ? r.data.total : items.length);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const add = async () => {
    if (!form.pin_code || !form.city || !form.state) { toast.error("Fill all fields"); return; }
    try {
      await api.post("/pins", form);
      toast.success("PIN added");
      setForm({ pin_code: "", city: "", state: "", active: true });
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (id) => { await api.patch(`/pins/${id}`); load(); };
  const remove = async (id) => { await api.delete(`/pins/${id}`); load(); };

  const runCheck = async () => {
    if (!check) return;
    const r = await api.get(`/pins/check/${check}`);
    setCheckResult(r.data);
  };

  const handleXlsxImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Warn upfront for very large files
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const toastId = toast.loading(`Uploading ${sizeMB} MB PIN file — this can take a couple of minutes for 100k+ rows…`);
    try {
      const buffer = await file.arrayBuffer();
      const res = await api.post("/pins/import-xlsx", buffer, {
        headers: { "Content-Type": "application/octet-stream" },
        // 15-min ceiling for very large XLSX imports
        timeout: 15 * 60 * 1000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const d = res.data || {};
      toast.success(
        `Imported ${d.total_rows || 0} rows · +${d.added || 0} new · ${d.updated || 0} updated${
          d.duplicates_in_file ? ` · ${d.duplicates_in_file} duplicates skipped` : ""
        }`,
        { id: toastId, duration: 8000 }
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "XLSX import failed", { id: toastId });
    }
    e.target.value = "";
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps text-accent mb-2">Logistics</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">PIN serviceability</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Manage the areas where home sample collection is available. Bulk-upload via XLSX with columns: Sr No, City, State, Sub District, PIN Code, Locality, Zone.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label htmlFor="pin-xlsx-input" className="cursor-pointer">
            <input id="pin-xlsx-input" data-testid="pin-xlsx-input" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleXlsxImport}/>
            <span className="inline-flex items-center gap-1.5 border border-border hover:bg-secondary rounded-full h-10 px-4 text-sm">
              <MapPin size={16}/> Import XLSX
            </span>
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pin-btn" className="bg-primary hover:bg-primary/90 rounded-full"><Plus size={16} className="mr-1.5"/> Add PIN</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-2xl">Add serviceable PIN</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>PIN code</Label><Input data-testid="pin-code-input" value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} /></div>
              <div><Label>City</Label><Input data-testid="pin-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input data-testid="pin-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm">Active</span></div>
            </div>
            <DialogFooter><Button data-testid="save-pin-btn" onClick={add} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Card className="p-6 border-border">
        <div className="label-caps text-muted-foreground mb-3">Search & quick check</div>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input data-testid="pin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 11,000+ PINs by code, city, state, locality…" className="pl-10 h-11 rounded-lg" />
          </div>
          <div className="flex gap-3 md:w-auto w-full">
            <div className="relative flex-1 md:max-w-xs">
              <Input data-testid="pin-check-input" value={check} onChange={(e) => setCheck(e.target.value)} placeholder="Verify a PIN code" className="h-11 rounded-lg" />
            </div>
            <Button data-testid="pin-check-btn" onClick={runCheck} className="bg-primary hover:bg-primary/90 rounded-lg">Check</Button>
          </div>
        </div>
        {checkResult && (
          <div className="mt-3 inline-flex items-center px-4 py-2 rounded-lg" style={{ background: checkResult.serviceable ? "#DCFCE7" : "#FEE2E2" }}>
            <span className={`text-sm font-medium ${checkResult.serviceable ? "text-emerald-700" : "text-red-700"}`}>
              {checkResult.serviceable ? `Serviceable · ${checkResult.area?.city || ""}` : "Not serviceable"}
            </span>
          </div>
        )}
      </Card>

      <Card className="border-border overflow-hidden">
        <div className="px-6 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{pins.length}</span>
            {" "}of{" "}
            <span className="font-medium text-primary" data-testid="pin-total-count">{total.toLocaleString()}</span>
            {" "}PINs
          </div>
          {q && <div className="text-xs text-muted-foreground">Filtered by "{q}"</div>}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PIN</TableHead>
              <TableHead>City</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pins.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No PINs configured</TableCell></TableRow>}
            {pins.map((p) => (
              <TableRow key={p.id} data-testid={`pin-row-${p.id}`}>
                <TableCell className="font-mono">{p.pin_code}</TableCell>
                <TableCell><div className="flex items-center gap-2"><MapPin size={14} className="text-primary"/>{p.city}</div></TableCell>
                <TableCell>{p.state}</TableCell>
                <TableCell><Switch data-testid={`pin-toggle-${p.id}`} checked={p.active} onCheckedChange={() => toggle(p.id)} /></TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`pin-delete-${p.id}`} onClick={() => remove(p.id)} size="icon" variant="ghost" className="text-destructive hover:bg-red-50">
                    <Trash2 size={16}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
